import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Space,
  Button,
  Drawer,
  Input,
  Select,
  Checkbox
} from '@lockerpm/design';

import { useTranslation } from "react-i18next";

import scamReportServices from "../../../../services/scam-report";

import global from '../../../../config/global';

function ReportFormData(props) {
  const {
    visible = false,
    onClose = () => {},
    onReload = () => {}
  } = props

  const { t } = useTranslation()
  const [form] = Form.useForm()
  let entitySearchTimeout = null

  const [callingAPI, setCallingAPI] = useState(false);
  const [searching, setSearching] = useState(false);
  const [entityOptions, setEntityOptions] = useState([]);

  const scamType = Form.useWatch('type', form)
  const phishingType = Form.useWatch('phishing_type', form)

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        type: global.constants.SCAM_TYPE.Url,
        value: '',
        phishing_type: null,
        target_entity: null,
        description: '',
        is_anonymous: false
      })
    } else {
      form.resetFields();
      setCallingAPI(false);
    }
  }, [visible])

  const phishingTypeOptions = useMemo(() => {
    if (scamType === global.constants.SCAM_TYPE.Url) {
      return Object.values(global.constants.SCAM_URL_TYPE)
    }
    if (scamType === global.constants.SCAM_TYPE.Facebook) {
      return Object.values(global.constants.SCAM_FACEBOOK_TYPE)
    }
    if (scamType === global.constants.SCAM_TYPE.Phone) {
      return Object.values(global.constants.SCAM_URL_TYPE)
    }
    return []
  }, [scamType])

  const isShowEntity = useMemo(() => {
    if (scamType === global.constants.SCAM_TYPE.Url) {
      return global.constants.SCAM_URL_TYPE_FAKES.includes(phishingType)
    }
    if (scamType === global.constants.SCAM_TYPE.Facebook) {
      return global.constants.SCAM_FACEBOOK_TYPE_FAKES.includes(phishingType)
    }
    if (scamType === global.constants.SCAM_TYPE.Phone) {
      return global.constants.SCAM_PHONE_TYPE_FAKES.includes(phishingType)
    }
    return false
  }, [scamType, phishingType])

  const valueInfo = useMemo(() => {
    if (scamType === global.constants.SCAM_TYPE.Url) {
      return {
        title: 'enter_url',
        placeholder: 'enter_url_placeholder'
      }
    }
    if (scamType === global.constants.SCAM_TYPE.Facebook) {
      return {
        title: 'enter_facebook_url',
        placeholder: 'enter_facebook_url_placeholder'
      }
    }
    if (scamType === global.constants.SCAM_TYPE.Phone) {
      return {
        title: 'enter_phone',
        placeholder: 'enter_phone_placeholder'
      }
    }
    return {}
  }, [scamType])

  const handleSave = async () => {
    form.validateFields().then(async (values) => {
      setCallingAPI(true);
      await createReport(values);
      setCallingAPI(false);
      onClose();
    })
  }

  const searchEntityOptions = (text) => {
    if (!entitySearchTimeout) {
      setSearching(true)
      entitySearchTimeout = setTimeout(() => {
        scamReportServices.search_identity(text).then((res) => {
          setEntityOptions(res?.results?.map((r) => ({ value: r.name, label: r.name })));
        }).catch(() => {
          setEntityOptions([])
        }).finally(() => {
          setSearching(false)
        })
      }, 1000);
    } else {
      clearTimeout(entitySearchTimeout);
      entitySearchTimeout = null
    }
  }

  const createReport = async (values) => {
    const payload = {
      is_anonymous: values.is_anonymous,
      type: values.type,
      value: values.value,
      phishing_type: values.phishing_type,
      target_entity: values.target_entity || '',
      description: values.description || ''
    }
    scamReportServices.create_scam_report(payload).then(() => {
      onReload();
      onClose();
    }).catch((error) => {
      console.log(error);
      global.pushError(error)
    })
  }

  return (
    <div className={props.className}>
      <Drawer
        title={t('scam_my_report.form_data.title')}
        placement="right"
        onClose={onClose}
        open={visible}
        footer={
          <Space className='flex items-center justify-end'>
            <Button
              disabled={callingAPI}
              onClick={onClose}
            >
              {t('button.cancel')}
            </Button>
            <Button
              type="primary"
              loading={callingAPI}
              onClick={handleSave}
            >
            { t('scam_my_report.form_data.button.submit') } 
            </Button>
          </Space>
        }
      >
        <div className='flex flex-col gap-2'>
          <p>
            {t('scam_my_report.form_data.description')}
          </p>
          <Form
            form={form}
            layout="vertical"
            labelAlign={'left'}
            disabled={callingAPI}
            className='vault-form'
          >
            <Form.Item
              name={'type'}
              className='mb-2'
              label={
                <p className='font-semibold'>
                  {t('scam_my_report.form_data.form_item.category')}
                </p>
              }
              rules={[]}
            >
              <Select
                options={[
                  {
                    value: global.constants.SCAM_TYPE.Url,
                    label: t('scam_my_report.form_data.type.url')
                  },
                  {
                    value: global.constants.SCAM_TYPE.Facebook,
                    label: t('scam_my_report.form_data.type.facebook')
                  },
                  {
                    value: global.constants.SCAM_TYPE.Phone,
                    label: t('scam_my_report.form_data.type.phone')
                  },
                ]}
              />
            </Form.Item>
            <Form.Item
              name={'value'}
              className='mb-2'
              label={
                <p className='font-semibold'>
                  {t(`scam_my_report.form_data.form_item.${valueInfo.title}`)}
                </p>
              }
              rules={[
                global.rules.REQUIRED(t('common.value')),
                ({}) => ({
                  validator(_, value) {
                    if (value) {
                      const urlTest = global.patterns.LINK.test(value?.trim());
                      const phoneTest = global.patterns.PHONE.test(value?.trim());
                      const facebookProfileTest =  global.patterns.FACEBOOK_PROFILE_URL.test(value?.trim());
                      if (scamType === global.constants.SCAM_TYPE.Url) {
                        if (!urlTest || facebookProfileTest) {
                          return Promise.reject(new Error(t('validation.invalid', { name: t('common.value') })));
                        }
                      } else if (scamType === global.constants.SCAM_TYPE.Facebook) {
                        if (!facebookProfileTest) {
                          return Promise.reject(new Error(t('validation.invalid', { name: t('common.value') })));
                        }
                      } else if (scamType === global.constants.SCAM_TYPE.Phone) {
                        if (!phoneTest) {
                          return Promise.reject(new Error(t('validation.invalid', { name: t('common.value') })));
                        }
                      }
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input
                placeholder={t(`scam_my_report.form_data.form_item.${valueInfo.placeholder}`)}
              />
            </Form.Item>
            <Form.Item
              name={'phishing_type'}
              className='mb-2'
              label={
                <p className='font-semibold'>
                  {t('scam_my_report.form_data.form_item.type_of_scam')}
                </p>
              }
              rules={[
                global.rules.REQUIRED(t('common.value')),
              ]}
            >
              <Select
                placeholder={t('scam_my_report.form_data.form_item.type_of_scam_placeholder')}
                options={phishingTypeOptions.map((o) => ({
                  value: o,
                  label: t(`scam_type.${o}`)
                }))}
              />
            </Form.Item>
            {
              isShowEntity && <Form.Item
                name={'target_entity'}
                className='mb-2'
                label={
                  <p className='font-semibold'>
                    {t('scam_my_report.form_data.form_item.entity')}
                  </p>
                }
                rules={[]}
              >
                <Select
                  showSearch={true}
                  filterOption={false}
                  loading={searching}
                  options={entityOptions}
                  onSearch={(v) => searchEntityOptions(v)}
                  placeholder={t('scam_my_report.form_data.form_item.entity_placeholder')}
                />
              </Form.Item>
            }
            <Form.Item
              name={'description'}
              className='mb-2'
              label={
                <p className='font-semibold'>
                  {t('scam_my_report.form_data.form_item.description')}
                </p>
              }
              rules={[]}
            >
              <Input.TextArea
                autoSize={{ minRows: 4 }}
                placeholder={t('scam_my_report.form_data.form_item.description')}
              />
            </Form.Item>
            <Form.Item
              name={'is_anonymous'}
              className='mb-2'
              label={''}
              valuePropName="checked"
              rules={[]}
            >
              <Checkbox>
                {t('scam_my_report.form_data.form_item.post_anonymously')}
              </Checkbox>
            </Form.Item>
          </Form>
        </div>
      </Drawer>
    </div>
  );
}

export default ReportFormData;
